import React, { useState } from "react";
import Popover from "../../../components/Popover";
import { useParams } from "react-router-dom";
import { useQueryGetExamPaperStatus } from "@/apis/exam_progress/getExamPaperStatus";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import HeaderButton from "./HeaderButton";
import {
  EXAM_PAPER_TYPE_READING_WRITING,
  EXAM_PAPER_TYPE_MATH,
} from "@/constants/exam";
import { useExamContext } from "../context/ExamContext";
import { VariableIcon } from "@heroicons/react/20/solid";
import { getPaperTypeLabel } from "@/constants/exam";
import { ClockIcon } from "@heroicons/react/24/outline";

// 북마크 outline SVG
const BookmarkIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    stroke="#222"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block align-middle mr-1"
    style={{ marginTop: "-2px" }}
  >
    <path d="M5 3.5A1.5 1.5 0 0 1 6.5 2h7A1.5 1.5 0 0 1 15 3.5v13.13a.5.5 0 0 1-.79.41l-4.21-2.81-4.21 2.81A.5.5 0 0 1 5 16.63V3.5z" />
  </svg>
);

// 세로 chevron 아이콘 SVG
const ChevronDown = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block align-middle ml-1"
  >
    <polyline points="4,6 8,10 12,6" />
  </svg>
);

const ChevronUp = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block align-middle ml-1"
  >
    <polyline points="4,10 8,6 12,10" />
  </svg>
);

// 계산기 아이콘 SVG
const CalculatorIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="2" width="18" height="20" rx="2" fill="none" />
    <rect x="7" y="5" width="10" height="3" rx="1" fill="none" />
    <circle cx="7.5" cy="11.5" r="1" />
    <circle cx="12" cy="11.5" r="1" />
    <circle cx="16.5" cy="11.5" r="1" />
    <circle cx="7.5" cy="16" r="1" />
    <circle cx="12" cy="16" r="1" />
    <circle cx="16.5" cy="16" r="1" />
  </svg>
);

// More(점3개) 아이콘 SVG
const DotsVerticalIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#222"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="6" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="18" r="1.5" />
  </svg>
);

interface ExamHeaderProps {
  timer: string;
  timerHidden: boolean;
  onToggleTimerHidden: () => void;
  calculatorOpen: boolean;
  onToggleCalculator: () => void;
  moreOpen: boolean;
  onToggleMore: () => void;
  moreMenu?: React.ReactNode;
  timerColor?: string;
}

const ExamHeader: React.FC<ExamHeaderProps> = ({
  timer,
  timerHidden,
  onToggleTimerHidden,
  calculatorOpen,
  onToggleCalculator,
  moreOpen,
  onToggleMore,
  moreMenu,
  timerColor = "text-[#22336b]",
}) => {
  const { id, exam_paper_id } = useParams<{
    id: string;
    exam_paper_id: string;
  }>();
  const { data: progressStatus } = useQueryGetExamPaperStatus(
    id || "",
    exam_paper_id || ""
  );
  const { data: examConfig } = useQueryGetExamConfig(
    progressStatus?.paper?.exam_type || ""
  );
  const { isReferenceModalOpen, setIsReferenceModalOpen } = useExamContext();
  const [directionsOpen, setDirectionsOpen] = useState(false);

  // Check if calculator is allowed for current subject
  const isCalculatorAllowed = React.useMemo(() => {
    if (!examConfig?.subjects || !progressStatus?.paper?.subject) return false;
    const subjectConfig = examConfig.subjects.find(
      (s) => s.subject === progressStatus?.paper?.subject
    );
    return subjectConfig?.calculator_allowed || false;
  }, [examConfig, progressStatus?.paper?.subject]);

  return (
    <>
      <div className="grid grid-cols-3 items-center px-8 py-3 bg-[#e6edf8] custom-dashed-border">
        {/* 좌측: 시험명 + Directions */}
        <div className="flex flex-col items-start justify-center relative">
          <div className="text-lg font-bold text-[#22336b] tracking-tight text-left">
            {progressStatus?.paper
              ? progressStatus?.paper?.exam_type === "SAT"
                ? `${getPaperTypeLabel(
                    progressStatus?.paper?.subject ?? ""
                  )}, Module ${progressStatus.paper.current_module || 1}`
                : getPaperTypeLabel(progressStatus?.paper?.subject ?? "")
              : ""}
          </div>
          {progressStatus?.paper?.exam_type === "SAT" && (
            <Popover
              className="max-w-[880px font-serif]"
              open={directionsOpen}
              onOpenChange={setDirectionsOpen}
              placement="bottom-start"
              title="Directions"
              trigger={
                <button className="text-sm text-black font-medium flex items-center mt-1 px-0 py-0 focus:outline-none cursor-pointer">
                  <span className="flex items-center">
                    Directions{directionsOpen ? <ChevronUp /> : <ChevronDown />}
                  </span>
                </button>
              }
            >
              {progressStatus?.paper?.subject ===
                EXAM_PAPER_TYPE_READING_WRITING && (
                <>
                  <p className="text-lg">
                    The questions in this section address a number of important
                    reading and writing skills. Each question includes one or
                    more passages, which may include a table or graph. Read each
                    passage and question carefully, and then choose the best
                    answer to the question based on the passage(s). <br />
                    <br />
                    All questions in this section are multiple-choice with four
                    answer choices. Each question has a single best answer.
                  </p>
                </>
              )}
              {progressStatus?.paper?.subject === EXAM_PAPER_TYPE_MATH && (
                <>
                  <p className="text-lg">
                    The questions in this section address a number of important
                    math skills.
                    <br />
                    Use of a calculator is permitted for all questions. A
                    reference sheet, calculator, and these directions can be
                    accessed throughout the test.
                    <br />
                    Unless otherwise indicated:
                    <br />
                    • All variables and expressions represent real numbers.
                    <br />
                    • Figures provided are drawn to scale.
                    <br />
                    • All figures lie in a plane.
                    <br />
                    • The domain of a given function f is the set of all real
                    numbers x for which f(x) is a real number.
                    <br />
                    For multiple-choice questions, solve each problem and choose
                    the correct answer from the choices provided. Each
                    multiple-choice question has a single correct answer.
                    <br />
                    For student-produced response questions, solve each problem
                    and enter your answer as described below.
                    <br />
                    • If you find more than one correct answer, enter only one
                    answer.
                    <br />
                    • You can enter up to 5 characters for a positive answer and
                    up to 6 characters (including the negative sign) for a
                    negative answer.
                    <br />
                    • If your answer is a fraction that doesn't fit in the
                    provided space, enter the decimal equivalent.
                    <br />
                    • If your answer is a decimal that doesn't fit in the
                    provided space, enter it by truncating or rounding at the
                    fourth digit.
                    <br />
                    • If your answer is a mixed number (such as 3 1/2), enter it
                    as an improper fraction (7/2) or its decimal equivalent
                    (3.5).
                    <br />
                    • Don't enter symbols such as a percent sign, comma, or
                    dollar sign.
                    <br />
                  </p>
                </>
              )}
            </Popover>
          )}
        </div>

        {/* 중앙: 타이머 + Hide/Show 토글 */}
        <div className="flex flex-col items-center justify-center">
          <div
            className={`text-xl font-extrabold tracking-widest text-center h-[28px] flex items-center justify-center ${
              timerColor ?? "text-[#22336b]"
            }`}
          >
            {timerHidden ? <ClockIcon /> : timer}
          </div>
          <button
            className="mt-2 px-4 py-0.5 border border-[#222] rounded-full text-sm font-medium flex items-center justify-center bg-white hover:bg-gray-100 transition-colors min-h-[28px] min-w-[60px] cursor-pointer"
            onClick={onToggleTimerHidden}
          >
            {timerHidden ? "Show" : "Hide"}
          </button>
        </div>

        {/* 우측: Calculator + More 버튼 */}
        <div className="flex flex-row items-center justify-end gap-2 relative">
          {isCalculatorAllowed && (
            <HeaderButton
              icon={<CalculatorIcon />}
              label="Calculator"
              onClick={onToggleCalculator}
              ariaLabel="Toggle Calculator"
            />
          )}
          {progressStatus?.paper?.subject === EXAM_PAPER_TYPE_MATH && (
            <Popover
              className="max-w-[880px] font-serif"
              open={isReferenceModalOpen}
              onOpenChange={setIsReferenceModalOpen}
              placement="bottom-end"
              title="Reference"
              trigger={
                <HeaderButton
                  icon={<VariableIcon className="w-6 h-6" />}
                  label="Reference"
                  onClick={() => setIsReferenceModalOpen(!isReferenceModalOpen)}
                  ariaLabel="Toggle Reference"
                />
              }
            >
              <div className="p-4">
                <img
                  src="/img_reference_shape_wide.png"
                  alt="Reference Sheet"
                  className="w-full h-auto"
                />
              </div>
              <p>
                The number of degrees of arc in a circle is 360. <br />
                The number of radians of arc in a circle is 2π. <br />
                The sum of the measures in degrees of the angles of a triangle
                is 180.
              </p>
            </Popover>
          )}
          <HeaderButton
            icon={<DotsVerticalIcon />}
            label="More"
            onClick={onToggleMore}
            ariaLabel="More"
          />
          {moreMenu}
        </div>
      </div>
    </>
  );
};

export default ExamHeader;
